import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, MapPin, Users, Phone, Mail, Building2, User, Globe, Loader2, Upload, Link, X, ImageIcon, MessageSquare, Shield, CheckCircle, Store, Calendar, Clock } from 'lucide-react';
import { toast } from 'sonner';
import AddressAutocomplete from '../components/AddressAutocomplete';

const API = process.env.REACT_APP_BACKEND_URL || '';

const MyProfile = () => {
  const { token, user, isDemo } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    company_name: '',
    tagline: '',
    website: '',
    logo: '',
    background_image: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    phone: '',
    compliance_number: '',
    gps_lat: 0,
    gps_lng: 0,
    show_on_map: false,
    show_company_name: false,
    show_name: false,
    show_address: false,
    show_email: false,
    show_phone: false,
    hide_yard_location: false,
    show_exact_location: false,
    offers_swarm_rescue: false,
    offers_mentoring: false,
    is_bee_club: false,
    is_beekeeping_supplies: false,
    is_queen_supplier: false,
    hive_mind_enabled: false,
    activity_history: [],
    terms_accepted: false,
    terms_accepted_at: null,
    terms_version: ''
  });

  const [clubData, setClubData] = useState(null);
  const [clubForm, setClubForm] = useState({
    club_name: '', description: '', website: '', email: '', phone: '',
    address: '', city: '', state: '', postcode: '', country: 'Australia',
    meeting_day: '', meeting_time: '', meeting_frequency: '', meeting_location: '',
    member_count: '', established_year: '', social_facebook: '', social_instagram: '',
    logo_url: '', header_image_url: '', has_authorization: false, is_public: true,
  });
  const [clubSaving, setClubSaving] = useState(false);

  useEffect(() => {
    if (isDemo) {
      navigate('/');
      return;
    }
    fetchProfile();
    fetchClubData();
  }, [isDemo, navigate]);

  const fetchClubData = async () => {
    try {
      const res = await fetch(`${API}/api/my-club`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        if (data && data.club_name) {
          setClubData(data);
          setClubForm(prev => ({ ...prev, ...data }));
        }
      }
    } catch {}
  };

  const saveClubData = async () => {
    setClubSaving(true);
    try {
      const res = await fetch(`${API}/api/my-club`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(clubForm)
      });
      if (res.ok) {
        toast.success('Club submitted for review! Once approved, it will appear on the public directory.');
        fetchClubData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save club');
      }
    } catch { toast.error('Failed to save'); }
    finally { setClubSaving(false); }
  };

  const handleClubImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/products/upload-image`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (res.ok) {
        const data = await res.json();
        setClubForm(prev => ({ ...prev, [field]: `${API}/api/files/${data.path}` }));
        toast.success('Image uploaded');
      }
    } catch { toast.error('Upload failed'); }
  };

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Extract show_exact_location from nested public_listing if present
        if (data.public_listing?.show_exact_location !== undefined) {
          data.show_exact_location = data.public_listing.show_exact_location;
        }
        setProfile(prev => ({ ...prev, ...data }));
      } else if (response.status === 401) {
        toast.error('Session expired. Please log in again.');
        localStorage.removeItem('token');
        navigate('/');
        return;
      } else {
        toast.error('Failed to load profile');
      }
    } catch (err) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const geocodeAddress = async () => {
    const fullAddress = [profile.address, profile.city, profile.state, profile.postal_code, profile.country]
      .filter(Boolean).join(', ');
    
    if (!fullAddress) {
      toast.error('Please enter an address first');
      return;
    }

    setGeocoding(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        setProfile(prev => ({
          ...prev,
          gps_lat: parseFloat(data[0].lat),
          gps_lng: parseFloat(data[0].lon)
        }));
        toast.success('Location found!');
      } else {
        toast.error('Could not find location. Please check address.');
      }
    } catch (err) {
      toast.error('Geocoding failed');
    } finally {
      setGeocoding(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API}/api/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({ ...prev, logo: data.file_id }));
        toast.success('Logo uploaded!');
      }
    } catch (err) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingBackground(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API}/api/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({ ...prev, background_image: data.file_id }));
        toast.success('Background image uploaded!');
      }
    } catch (err) {
      toast.error('Failed to upload background image');
    } finally {
      setUploadingBackground(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Update profile info
      const profileResponse = await fetch(`${API}/api/auth/me`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: profile.name,
          company_name: profile.company_name,
          tagline: profile.tagline,
          website: profile.website,
          logo: profile.logo,
          background_image: profile.background_image,
          address: profile.address,
          city: profile.city,
          state: profile.state,
          postal_code: profile.postal_code,
          country: profile.country,
          phone: profile.phone,
          compliance_number: profile.compliance_number
        })
      });

      // Update visibility settings
      const visibilityResponse = await fetch(`${API}/api/auth/me/visibility`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          gps_lat: profile.gps_lat,
          gps_lng: profile.gps_lng,
          show_on_map: profile.show_on_map,
          show_company_name: profile.show_company_name,
          show_name: profile.show_name,
          show_address: profile.show_address,
          show_email: profile.show_email,
          show_phone: profile.show_phone,
          offers_swarm_rescue: profile.offers_swarm_rescue,
          offers_mentoring: profile.offers_mentoring,
          is_bee_club: profile.is_bee_club,
          is_beekeeping_supplies: profile.is_beekeeping_supplies,
          is_queen_supplier: profile.is_queen_supplier,
          hive_mind_enabled: profile.hive_mind_enabled,
          hide_yard_location: profile.hide_yard_location,
          show_exact_location: profile.show_exact_location,
          public_listing_enabled: profile.public_listing_enabled || false
        })
      });

      if (profileResponse.ok && visibilityResponse.ok) {
        toast.success('Profile saved successfully!');
        // Scroll to top of page after save
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (profileResponse.status === 401 || visibilityResponse.status === 401) {
        toast.error('Session expired. Please log in again.');
        localStorage.removeItem('token');
        navigate('/');
      } else {
        // Show which part failed
        const errors = [];
        if (!profileResponse.ok) {
          const detail = await profileResponse.json().catch(() => ({}));
          errors.push(`Profile: ${detail.detail || profileResponse.statusText}`);
        }
        if (!visibilityResponse.ok) {
          const detail = await visibilityResponse.json().catch(() => ({}));
          errors.push(`Visibility: ${detail.detail || visibilityResponse.statusText}`);
        }
        toast.error(`Failed to save: ${errors.join(', ')}`);
      }
    } catch (err) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const hasValidAddress = profile.city && profile.gps_lat !== 0 && profile.gps_lng !== 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <User className="h-6 w-6 text-amber-600" />
            My Profile
          </h1>
        </div>

        {/* Profile Info */}
        <div className="bg-white rounded-2xl shadow-lg border border-stone-200 p-6 mb-6">
          <h2 className="font-bold text-stone-800 mb-4 flex items-center gap-2">
            <User className="w-5 h-5" /> Personal Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Name</label>
              <Input
                value={profile.name}
                onChange={e => setProfile({...profile, name: e.target.value})}
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Phone</label>
              <Input
                value={profile.phone}
                onChange={e => setProfile({...profile, phone: e.target.value})}
                placeholder="+1 555-123-4567"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-stone-700 mb-1">Email</label>
              <Input value={user?.email || ''} disabled className="bg-stone-50" />
              <p className="text-xs text-stone-400 mt-1">Contact admin to change email</p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-stone-700 mb-1">Beekeeper Registration / Compliance Number</label>
              <Input
                value={profile.compliance_number || ''}
                onChange={e => setProfile({...profile, compliance_number: e.target.value})}
                placeholder="e.g. BK-12345 (from your local authority)"
              />
              <p className="text-xs text-stone-400 mt-1">Registration number required by your local authority to keep bees (if applicable)</p>
            </div>
          </div>
        </div>

        {/* Company Profile */}
        <div className="bg-white rounded-2xl shadow-lg border border-stone-200 p-6 mb-6">
          <h2 className="font-bold text-stone-800 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5" /> Company / Apiary Profile
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-stone-700 mb-1">Company/Apiary Name</label>
              <Input
                value={profile.company_name}
                onChange={e => setProfile({...profile, company_name: e.target.value})}
                placeholder="Your apiary name"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-stone-700 mb-1">Tagline</label>
              <Textarea
                value={profile.tagline || ''}
                onChange={e => setProfile({...profile, tagline: e.target.value})}
                placeholder="A short description of your apiary..."
                rows={2}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-stone-700 mb-1">Website URL</label>
              <div className="relative">
                <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  value={profile.website || ''}
                  onChange={e => setProfile({...profile, website: e.target.value})}
                  placeholder="https://www.yourapiary.com"
                  className="pl-10"
                />
              </div>
            </div>
            
            {/* Logo Upload */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-stone-700 mb-2">Logo</label>
              <div className="flex items-center gap-4">
                {profile.logo ? (
                  <div className="relative">
                    <img 
                      src={`${API}/api/uploads/${profile.logo}`} 
                      alt="Company Logo" 
                      className="w-20 h-20 object-contain rounded-xl border border-stone-200 bg-stone-50"
                    />
                    <button
                      type="button"
                      onClick={() => setProfile({...profile, logo: ''})}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-20 h-20 border-2 border-dashed border-stone-300 rounded-xl flex items-center justify-center bg-stone-50">
                    <Building2 className="w-8 h-8 text-stone-300" />
                  </div>
                )}
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    disabled={uploadingLogo}
                  />
                  <Button type="button" variant="outline" disabled={uploadingLogo} asChild>
                    <span>
                      {uploadingLogo ? (
                        <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Uploading...</>
                      ) : (
                        <><Upload className="w-4 h-4 mr-2" /> {profile.logo ? 'Change Logo' : 'Upload Logo'}</>
                      )}
                    </span>
                  </Button>
                </label>
              </div>
            </div>

            {/* Background Image Upload */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-stone-700 mb-2">App Background Image</label>
              <p className="text-xs text-stone-500 mb-2">This image will appear as the background on your dashboard</p>
              <div className="flex items-center gap-4">
                {profile.background_image ? (
                  <div className="relative">
                    <img 
                      src={`${API}/api/uploads/${profile.background_image}`} 
                      alt="Background" 
                      className="w-32 h-20 object-cover rounded-xl border border-stone-200"
                    />
                    <button
                      type="button"
                      onClick={() => setProfile({...profile, background_image: ''})}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-32 h-20 border-2 border-dashed border-stone-300 rounded-xl flex items-center justify-center bg-stone-50">
                    <ImageIcon className="w-8 h-8 text-stone-300" />
                  </div>
                )}
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBackgroundUpload}
                    className="hidden"
                    disabled={uploadingBackground}
                  />
                  <Button type="button" variant="outline" disabled={uploadingBackground} asChild>
                    <span>
                      {uploadingBackground ? (
                        <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Uploading...</>
                      ) : (
                        <><Upload className="w-4 h-4 mr-2" /> {profile.background_image ? 'Change Background' : 'Upload Background'}</>
                      )}
                    </span>
                  </Button>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-white rounded-2xl shadow-lg border border-stone-200 p-6 mb-6">
          <h2 className="font-bold text-stone-800 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5" /> Address
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Street Address</label>
              <AddressAutocomplete
                value={profile.address || ''}
                onChange={({ address, lat, lon, suburb, state, postcode }) => {
                  setProfile(prev => ({
                    ...prev,
                    address: address || prev.address,
                    city: suburb || prev.city,
                    state: state || prev.state,
                    postal_code: postcode || prev.postal_code,
                    gps_lat: lat || prev.gps_lat,
                    gps_lng: lon || prev.gps_lng,
                  }));
                }}
                placeholder="Start typing your address..."
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">City</label>
                <Input
                  value={profile.city}
                  onChange={e => setProfile({...profile, city: e.target.value})}
                  placeholder="Melbourne"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">State/Province</label>
                <Input
                  value={profile.state}
                  onChange={e => setProfile({...profile, state: e.target.value})}
                  placeholder="VIC"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Postal Code</label>
                <Input
                  value={profile.postal_code}
                  onChange={e => setProfile({...profile, postal_code: e.target.value})}
                  placeholder="3000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Country</label>
                <Input
                  value={profile.country}
                  onChange={e => setProfile({...profile, country: e.target.value})}
                  placeholder="Australia"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <Button 
              variant="outline" 
              onClick={geocodeAddress}
              disabled={geocoding}
              className="flex items-center gap-2 shrink-0"
            >
              {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
              {geocoding ? 'Finding...' : 'Get GPS Coordinates'}
            </Button>
            {profile.gps_lat !== 0 && (
              <span className="text-sm text-green-600 break-all">
                ✓ Location: {profile.gps_lat.toFixed(4)}, {profile.gps_lng.toFixed(4)}
              </span>
            )}
          </div>
        </div>

        {/* Community Map Settings */}
        <div className="bg-white rounded-2xl shadow-lg border border-stone-200 p-6 mb-6">
          <h2 className="font-bold text-stone-800 mb-2 flex items-center gap-2">
            <Users className="w-5 h-5" /> Community Beekeepers Map
          </h2>
          <p className="text-sm text-stone-500 mb-4">
            Show your details on the community map so other beekeepers can find you.
          </p>

          {!hasValidAddress && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-amber-700">
                ⚠️ Please enter your address and click "Get GPS Coordinates" to appear on the map.
              </p>
            </div>
          )}

          <div className="space-y-3">
            <label className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl cursor-pointer hover:bg-stone-100">
              <input
                type="checkbox"
                checked={profile.show_on_map}
                onChange={e => setProfile({...profile, show_on_map: e.target.checked})}
                disabled={!hasValidAddress}
                className="w-5 h-5 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
              />
              <div>
                <span className="font-medium text-stone-800">Show me on the Community Map</span>
                <p className="text-xs text-stone-500">Your pin will appear on the "Find Beekeepers Near Me" map</p>
              </div>
            </label>

            {profile.show_on_map && (
              <div className="ml-8 space-y-2 border-l-2 border-amber-200 pl-4">
                <p className="text-sm font-medium text-stone-600 mb-2">Choose what details to show:</p>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={profile.show_name}
                    onChange={e => setProfile({...profile, show_name: e.target.checked})}
                    className="w-4 h-4 rounded border-stone-300 text-amber-600"
                  />
                  <span className="text-sm text-stone-700">Beekeeper Name</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={profile.show_company_name}
                    onChange={e => setProfile({...profile, show_company_name: e.target.checked})}
                    className="w-4 h-4 rounded border-stone-300 text-amber-600"
                  />
                  <span className="text-sm text-stone-700">Company/Apiary Name</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={profile.show_address}
                    onChange={e => setProfile({...profile, show_address: e.target.checked})}
                    className="w-4 h-4 rounded border-stone-300 text-amber-600"
                  />
                  <span className="text-sm text-stone-700">City/State/Country</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={profile.show_email}
                    onChange={e => setProfile({...profile, show_email: e.target.checked})}
                    className="w-4 h-4 rounded border-stone-300 text-amber-600"
                  />
                  <span className="text-sm text-stone-700">Email Address</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={profile.show_phone}
                    onChange={e => setProfile({...profile, show_phone: e.target.checked})}
                    className="w-4 h-4 rounded border-stone-300 text-amber-600"
                  />
                  <span className="text-sm text-stone-700">Phone Number</span>
                </label>

                {/* Privacy option for yard location */}
                <div className="mt-4 pt-4 border-t border-amber-100">
                  <label className="flex items-start gap-2 cursor-pointer p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100">
                    <input
                      type="checkbox"
                      checked={profile.hide_yard_location}
                      onChange={e => setProfile({...profile, hide_yard_location: e.target.checked})}
                      className="w-5 h-5 mt-0.5 rounded border-stone-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="font-medium text-stone-800">🔒 Hide Exact Apiary Location</span>
                      <p className="text-xs text-stone-500 mt-1">
                        Your "Happening Now" activities will still be visible, but your exact yard location 
                        will be hidden on the map. Other beekeepers will only see your general area (~10km radius).
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contact Preferences */}
        <div className="bg-white rounded-2xl shadow-lg border border-stone-200 p-6 mb-6">
          <h2 className="font-bold text-stone-800 mb-2 flex items-center gap-2">
            <Phone className="w-5 h-5" /> Contact Preferences
          </h2>
          <p className="text-sm text-stone-500 mb-4">
            Other beekeepers can contact you if you select any options below:
          </p>

          <div className="space-y-3">
            <label className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded-xl cursor-pointer hover:bg-orange-100">
              <input
                type="checkbox"
                checked={profile.offers_swarm_rescue}
                onChange={e => setProfile({...profile, offers_swarm_rescue: e.target.checked})}
                className="w-5 h-5 rounded border-stone-300 text-orange-600 focus:ring-orange-500"
              />
              <div>
                <span className="font-medium text-stone-800">🐝 Swarm Rescue</span>
                <p className="text-xs text-stone-500">I'm available to collect swarms in my area</p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl cursor-pointer hover:bg-blue-100">
              <input
                type="checkbox"
                checked={profile.offers_mentoring}
                onChange={e => setProfile({...profile, offers_mentoring: e.target.checked})}
                className="w-5 h-5 rounded border-stone-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-stone-800">📚 Mentor</span>
                <p className="text-xs text-stone-500">I'm willing to mentor new beekeepers</p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-xl cursor-pointer hover:bg-purple-100">
              <input
                type="checkbox"
                checked={profile.is_bee_club}
                onChange={e => setProfile({...profile, is_bee_club: e.target.checked})}
                className="w-5 h-5 rounded border-stone-300 text-purple-600 focus:ring-purple-500"
                data-testid="is-bee-club-toggle"
              />
              <div>
                <span className="font-medium text-stone-800">🏛️ Bee Club / Association</span>
                <p className="text-xs text-stone-500">I represent a beekeeping club or association</p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl cursor-pointer hover:bg-green-100">
              <input
                type="checkbox"
                checked={profile.is_beekeeping_supplies}
                onChange={e => setProfile({...profile, is_beekeeping_supplies: e.target.checked})}
                className="w-5 h-5 rounded border-stone-300 text-green-600 focus:ring-green-500"
              />
              <div>
                <span className="font-medium text-stone-800">🛒 Beekeeping Supplies</span>
                <p className="text-xs text-stone-500">I sell beekeeping equipment and supplies</p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl cursor-pointer hover:bg-amber-100">
              <input
                type="checkbox"
                checked={profile.is_queen_supplier}
                onChange={e => setProfile({...profile, is_queen_supplier: e.target.checked})}
                className="w-5 h-5 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
              />
              <div>
                <span className="font-medium text-stone-800">👑 Queen Supplier</span>
                <p className="text-xs text-stone-500">I breed and sell queen bees</p>
              </div>
            </label>
          </div>
        </div>

        {/* Public Honey Listing */}
        <div className="bg-white rounded-2xl shadow-lg border border-stone-200 p-6 mb-6">
          <h2 className="font-bold text-stone-800 mb-2 flex items-center gap-2">
            <Store className="w-5 h-5 text-amber-600" /> Public Honey Listing
          </h2>
          <p className="text-sm text-stone-500 mb-4">
            List yourself on the public <strong>"Find Local Honey"</strong> page so customers can discover your products. 
            Your profile name, company, phone, email, and location from above will be used — no extra info needed.
          </p>

          <label className="flex items-center gap-3 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl cursor-pointer hover:bg-amber-100 transition-colors">
            <input
              type="checkbox"
              checked={profile.public_listing_enabled || false}
              onChange={e => setProfile({...profile, public_listing_enabled: e.target.checked})}
              className="w-5 h-5 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
            />
            <div>
              <span className="font-semibold text-stone-800">Show me on "Find Local Honey"</span>
              <p className="text-xs text-stone-500 mt-0.5">
                Customers searching for local honey will see your profile and products at <strong>/find-honey</strong>
              </p>
            </div>
          </label>

          {profile.public_listing_enabled && (
            <div className="mt-3 ml-8 space-y-3 border-l-2 border-amber-200 pl-4">
              <p className="text-sm font-medium text-stone-600 mb-2">What public visitors will see:</p>
              <div className="text-sm text-stone-600 space-y-1">
                <p>Name: <strong>{profile.company_name || profile.name || 'Not set'}</strong></p>
                <p>Location: <strong>{profile.city ? `${profile.city}${profile.state ? `, ${profile.state}` : ''}` : 'Not set — add your address above'}</strong> <span className="text-stone-400">(suburb only)</span></p>
                {profile.show_phone && profile.phone && <p>Phone: <strong>{profile.phone}</strong></p>}
                {profile.show_email && <p>Email: <strong>Visible</strong></p>}
              </div>

              {/* Exact Location Opt-in */}
              <div className="mt-3 pt-3 border-t border-stone-200">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={profile.show_exact_location || false}
                    onChange={e => setProfile({...profile, show_exact_location: e.target.checked})}
                    className="w-5 h-5 mt-0.5 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
                  />
                  <div>
                    <span className="font-semibold text-stone-800 text-sm">Show my exact location on the map</span>
                    <p className="text-xs text-stone-500 mt-0.5">
                      Enable this if you sell from a shop or welcome visitors. Your <strong>exact address</strong> will be pinned on the public map.
                    </p>
                    {!profile.show_exact_location && (
                      <p className="text-xs text-green-700 mt-1 bg-green-50 rounded px-2 py-1 inline-block">
                        Currently showing suburb only — your exact address is hidden
                      </p>
                    )}
                    {profile.show_exact_location && (
                      <p className="text-xs text-amber-700 mt-1 bg-amber-50 rounded px-2 py-1 inline-block">
                        Your exact address will be visible as a pin on the public map
                      </p>
                    )}
                  </div>
                </label>
              </div>

              <p className="text-xs text-stone-400 mt-2">
                Your active products from the Products page will be shown automatically.
              </p>
            </div>
          )}
        </div>

        {/* Hive Mind Community */}
        <div className="bg-white rounded-2xl shadow-lg border border-stone-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-amber-100 rounded-lg">
              <MessageSquare className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-lg font-bold text-stone-800">🧠 Hive Mind Community</h2>
          </div>
          <p className="text-sm text-stone-600 mb-4">
            Join the Hive Mind - a community forum where beekeepers share knowledge, ask questions, and connect with each other.
          </p>
          
          {/* Community Guidelines */}
          <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 mb-4 text-sm">
            <h3 className="font-bold text-stone-800 mb-2">📋 Community Policies & Protocols</h3>
            <div className="space-y-2 text-stone-600">
              <p><strong>✅ DO:</strong> Be respectful, kind, and helpful to all members. Support fellow beekeepers and share your knowledge.</p>
              <p><strong>❌ DON'T:</strong> Post offensive, rude, or disrespectful content. No spam, personal attacks, or misleading information.</p>
              <p><strong>⚠️ ENFORCEMENT:</strong> Admins may hide/delete inappropriate content. Repeated violations = suspension. We maintain a <span className="font-bold text-amber-700">strict, no-nonsense, friendly and kind only</span> community.</p>
            </div>
          </div>
          
          <label className="flex items-center gap-3 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-xl cursor-pointer hover:border-amber-400">
            <input
              type="checkbox"
              checked={profile.hive_mind_enabled}
              onChange={e => setProfile({...profile, hive_mind_enabled: e.target.checked})}
              className="w-6 h-6 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
            />
            <div>
              <span className="font-bold text-amber-900 text-lg">I Agree & Enable Hive Mind Access</span>
              <p className="text-sm text-amber-700">By enabling, you agree to follow our community guidelines</p>
            </div>
          </label>
        </div>

        {/* Bee Club Section */}
        {profile.is_bee_club && (
          <div className="bg-white rounded-2xl shadow-lg border border-stone-200 p-6 mt-6">
            <h2 className="text-lg font-bold text-stone-800 mb-2 flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-600" /> Bee Club / Association
            </h2>
            <p className="text-sm text-stone-500 mb-4">Your club will be reviewed by our team before appearing on the public directory.</p>

            {/* Status banner */}
            {clubData?.status === 'pending' && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-sm text-amber-700 flex items-center gap-2">
                <Clock className="h-4 w-4 flex-shrink-0" /> Your club listing is pending review. You'll be notified once approved.
              </div>
            )}
            {clubData?.status === 'approved' && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 text-sm text-green-700 flex items-center gap-2">
                <Shield className="h-4 w-4 flex-shrink-0" /> Your club is approved and visible on the public directory.
              </div>
            )}
            {clubData?.status === 'rejected' && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-sm text-red-700">
                Your club listing was not approved.{clubData?.rejection_reason && <> Reason: <strong>{clubData.rejection_reason}</strong></>} Please update and resubmit.
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-stone-700">Club Name *</label>
                  <Input value={clubForm.club_name} onChange={e => setClubForm({...clubForm, club_name: e.target.value})} placeholder="e.g., Cranbourne Beekeepers Club" data-testid="club-name-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700">Website</label>
                  <Input value={clubForm.website} onChange={e => setClubForm({...clubForm, website: e.target.value})} placeholder="https://..." />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-700">Description</label>
                <Textarea value={clubForm.description} onChange={e => setClubForm({...clubForm, description: e.target.value})} placeholder="Tell us about your club..." className="min-h-[80px]" />
              </div>

              {/* Images */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-stone-700">Club Logo</label>
                  <label className="flex items-center justify-center gap-2 border-2 border-dashed border-stone-300 rounded-xl p-3 cursor-pointer hover:border-amber-400 mt-1">
                    <Upload className="h-4 w-4 text-stone-400" />
                    <span className="text-xs text-stone-500">Upload logo</span>
                    <input type="file" accept="image/*" className="hidden" onChange={e => handleClubImageUpload(e, 'logo_url')} />
                  </label>
                  {clubForm.logo_url && <img src={clubForm.logo_url.startsWith('http') ? clubForm.logo_url : `${API}${clubForm.logo_url}`} alt="" className="mt-2 h-12 rounded-lg object-contain" />}
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700">Header Image</label>
                  <label className="flex items-center justify-center gap-2 border-2 border-dashed border-stone-300 rounded-xl p-3 cursor-pointer hover:border-amber-400 mt-1">
                    <Upload className="h-4 w-4 text-stone-400" />
                    <span className="text-xs text-stone-500">Upload header</span>
                    <input type="file" accept="image/*" className="hidden" onChange={e => handleClubImageUpload(e, 'header_image_url')} />
                  </label>
                  {clubForm.header_image_url && <img src={clubForm.header_image_url.startsWith('http') ? clubForm.header_image_url : `${API}${clubForm.header_image_url}`} alt="" className="mt-2 h-16 rounded-lg object-cover w-full" />}
                </div>
              </div>

              {/* Contact */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><label className="text-sm font-medium text-stone-700">Email</label><Input value={clubForm.email} onChange={e => setClubForm({...clubForm, email: e.target.value})} placeholder="club@email.com" /></div>
                <div><label className="text-sm font-medium text-stone-700">Phone</label><Input value={clubForm.phone} onChange={e => setClubForm({...clubForm, phone: e.target.value})} placeholder="0400 000 000" /></div>
                <div><label className="text-sm font-medium text-stone-700">Postcode</label><Input value={clubForm.postcode} onChange={e => setClubForm({...clubForm, postcode: e.target.value})} placeholder="3000" /></div>
              </div>

              {/* Location */}
              <div>
                <label className="text-sm font-medium text-stone-700">Club Address</label>
                <AddressAutocomplete
                  value={clubForm.address || ''}
                  onChange={({ address, lat, lon, suburb, state, postcode }) => {
                    setClubForm(prev => ({
                      ...prev,
                      address: address || prev.address,
                      city: suburb || prev.city,
                      state: state || prev.state,
                      postcode: postcode || prev.postcode,
                      gps_lat: lat || prev.gps_lat,
                      gps_lng: lon || prev.gps_lng,
                    }));
                  }}
                  placeholder="Start typing club address..."
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><label className="text-sm font-medium text-stone-700">City/Suburb</label><Input value={clubForm.city} onChange={e => setClubForm({...clubForm, city: e.target.value})} /></div>
                <div><label className="text-sm font-medium text-stone-700">State/Region</label><Input value={clubForm.state} onChange={e => setClubForm({...clubForm, state: e.target.value})} placeholder="e.g. VIC, NSW, Western Cape" /></div>
                <div><label className="text-sm font-medium text-stone-700">Country</label><Input value={clubForm.country || ''} onChange={e => setClubForm({...clubForm, country: e.target.value})} placeholder="e.g. Australia, South Africa" /></div>
              </div>

              {/* Club GPS / Map Pin */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <Button
                  type="button" variant="outline" className="flex items-center gap-2 shrink-0"
                  onClick={async () => {
                    const addr = [clubForm.address, clubForm.city, clubForm.state, clubForm.postcode, clubForm.country].filter(Boolean).join(', ');
                    if (!addr || !clubForm.city) { toast.error('Please enter at least a city'); return; }
                    try {
                      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addr)}&limit=1`);
                      const d = await r.json();
                      if (d?.[0]) {
                        setClubForm(prev => ({ ...prev, gps_lat: parseFloat(d[0].lat), gps_lng: parseFloat(d[0].lon) }));
                        toast.success('Club location found!');
                      } else { toast.error('Could not find location'); }
                    } catch { toast.error('Geocoding failed'); }
                  }}
                  data-testid="club-geocode-btn"
                >
                  <Globe className="w-4 h-4" /> Get Map Pin
                </Button>
                {clubForm.gps_lat && clubForm.gps_lng ? (
                  <span className="text-sm text-green-600">Map pin set ({Number(clubForm.gps_lat).toFixed(4)}, {Number(clubForm.gps_lng).toFixed(4)})</span>
                ) : (
                  <span className="text-xs text-stone-400">Set a map pin so your club appears on the interactive map</span>
                )}
              </div>

              {/* Meeting Info */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2"><Calendar className="h-4 w-4" /> Meeting Schedule</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div><label className="text-xs font-medium text-stone-600">Day</label><Input value={clubForm.meeting_day} onChange={e => setClubForm({...clubForm, meeting_day: e.target.value})} placeholder="e.g., 2nd Saturday" /></div>
                  <div><label className="text-xs font-medium text-stone-600">Time</label><Input value={clubForm.meeting_time} onChange={e => setClubForm({...clubForm, meeting_time: e.target.value})} placeholder="e.g., 10:00 AM" /></div>
                  <div><label className="text-xs font-medium text-stone-600">Frequency</label><Input value={clubForm.meeting_frequency} onChange={e => setClubForm({...clubForm, meeting_frequency: e.target.value})} placeholder="e.g., Monthly" /></div>
                  <div><label className="text-xs font-medium text-stone-600">Location</label><Input value={clubForm.meeting_location} onChange={e => setClubForm({...clubForm, meeting_location: e.target.value})} placeholder="Hall name/address" /></div>
                </div>
              </div>

              {/* Extra */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><label className="text-sm font-medium text-stone-700">Members</label><Input type="number" value={clubForm.member_count} onChange={e => setClubForm({...clubForm, member_count: e.target.value})} placeholder="e.g., 45" /></div>
                <div><label className="text-sm font-medium text-stone-700">Est. Year</label><Input type="number" value={clubForm.established_year} onChange={e => setClubForm({...clubForm, established_year: e.target.value})} placeholder="e.g., 1985" /></div>
                <div><label className="text-sm font-medium text-stone-700">Facebook</label><Input value={clubForm.social_facebook} onChange={e => setClubForm({...clubForm, social_facebook: e.target.value})} placeholder="URL" /></div>
                <div><label className="text-sm font-medium text-stone-700">Instagram</label><Input value={clubForm.social_instagram} onChange={e => setClubForm({...clubForm, social_instagram: e.target.value})} placeholder="URL" /></div>
              </div>

              {/* Secondary Verification Contact */}
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-stone-700 mb-2 flex items-center gap-2"><Shield className="h-4 w-4 text-stone-500" /> Verification Contact (Admin Use Only)</h3>
                <p className="text-xs text-stone-400 mb-3">A secondary contact helps us verify your club is legitimate. This is not shown publicly.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><label className="text-xs font-medium text-stone-600">Verification Email</label><Input value={clubForm.verification_email || ''} onChange={e => setClubForm({...clubForm, verification_email: e.target.value})} placeholder="committee@club.org.au" /></div>
                  <div><label className="text-xs font-medium text-stone-600">Verification Phone</label><Input value={clubForm.verification_phone || ''} onChange={e => setClubForm({...clubForm, verification_phone: e.target.value})} placeholder="Secondary contact number" /></div>
                </div>
              </div>

              {/* Authorization */}
              <label className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4 cursor-pointer">
                <input type="checkbox" checked={clubForm.has_authorization} onChange={e => setClubForm({...clubForm, has_authorization: e.target.checked})} className="mt-1" data-testid="club-authorization-checkbox" />
                <div>
                  <span className="font-semibold text-stone-800 text-sm">I confirm I have authorization to represent this club</span>
                  <p className="text-xs text-stone-500 mt-0.5">I am an authorized committee member or have permission to list this club publicly.</p>
                </div>
              </label>

              <Button onClick={saveClubData} disabled={clubSaving} className="bg-amber-600 hover:bg-amber-700 text-white w-full" data-testid="save-club-btn">
                {clubSaving ? 'Submitting...' : clubData ? 'Update & Resubmit for Review' : 'Submit Club for Review'}
              </Button>
            </div>
          </div>
        )}

        {/* Terms & Conditions Acceptance Status */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-lg font-bold text-stone-800 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-600" /> Legal & Terms
          </h2>
          
          {profile.terms_accepted ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-semibold text-green-800">Terms & Conditions Accepted</p>
                  <p className="text-sm text-green-600">
                    Version {profile.terms_version || '1.0'} • Accepted on{' '}
                    {profile.terms_accepted_at 
                      ? new Date(profile.terms_accepted_at).toLocaleDateString('en-AU', {
                          day: 'numeric',
                          month: 'long', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : 'Date not recorded'}
                  </p>
                </div>
              </div>
              <div className="mt-3 text-xs text-green-700 bg-green-100 rounded-lg p-3">
                <p className="font-medium mb-1">You have agreed to:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Data storage on Emergent Labs servers</li>
                  <li>Privacy policy and data usage terms</li>
                  <li>No liability for data loss or service downtime</li>
                  <li>User responsibilities and community guidelines</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-amber-600" />
                <div>
                  <p className="font-semibold text-amber-800">Terms Not Yet Accepted</p>
                  <p className="text-sm text-amber-600">
                    You will be prompted to accept terms on your next login.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Spacer for fixed save button */}
        <div className="h-20" />
      </div>

      {/* Fixed Save Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-stone-200 py-3 px-4 z-50 md:pl-[200px]">
        <div className="max-w-4xl mx-auto">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white h-12 text-lg"
            data-testid="save-profile-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Profile'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MyProfile;
